import { API_ENDPOINTS } from '@/constants';
import {
  del,
  get,
  handleResponse,
  patch,
  post,
  put
} from '@/services/base.service';
import { handleError as appHandleError } from '@/utils/errorHandler';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { ApiResponse } from './types';

export const useGetCarsListQuery = (payload: string) =>
  useQuery({
    queryFn: async () => {
      try {
        const response = await get(`${API_ENDPOINTS.CARS_LIST}?${payload}`);
        return handleResponse(response);
      } catch (err: unknown) {
        appHandleError(err);
        // Return a default value to prevent undefined
        return { success: false, data: { items: [], total: 0 } };
      }
    },
    enabled: !!payload,
    refetchOnWindowFocus: true,
    queryKey: ['cars-list', payload]
  });
export const useCarDeleteMutation = () =>
  useMutation<ApiResponse, Error, number>({
    mutationFn: (userId: number) => {
      return del(`${API_ENDPOINTS.CARS_LIST}/${userId}`);
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });
export const useUpdateCarStatusMutation = () =>
  useMutation<ApiResponse, Error, { id: number; status: number }>({
    mutationFn: (payload: { id: number; status: number }) => {
      return patch(`${API_ENDPOINTS.CARS_LIST}/${payload.id}`, {
        status: payload.status
      });
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });

export const useAddCarMutation = () =>
  useMutation<ApiResponse, Error, Record<string, unknown>>({
    mutationFn: (payload: Record<string, unknown>) => {
      return post(`${API_ENDPOINTS.CARS_LIST}`, payload);
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });

export const useUpdateCarMutation = () =>
  useMutation<
    ApiResponse,
    Error,
    { id: number; data: Record<string, unknown> }
  >({
    mutationFn: (payload: { id: number; data: Record<string, unknown> }) => {
      return put(`${API_ENDPOINTS.CARS_LIST}/${payload.id}`, payload.data);
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });

export const useGetCarDetailsQuery = (id: number) =>
  useQuery({
    queryFn: async () => {
      try {
        const response = await get(`${API_ENDPOINTS.CARS_LIST}/${id}`);
        return handleResponse(response);
      } catch (err: unknown) {
        appHandleError(err);
        return { success: false, data: null };
      }
    },
    queryKey: ['car-details', id],
    enabled: id > 0,
    refetchOnWindowFocus: false
  });

export const useGetTransportRoster = (payload: string | null) =>
  useQuery({
    queryFn: async () => {
      try {
        const response = await get(
          `${API_ENDPOINTS.TRANSPORT_ROASTER}?${payload}`
        );
        return handleResponse(response);
      } catch (err: unknown) {
        appHandleError(err);
        return { success: false, data: null };
      }
    },
    queryKey: ['transport-roster', payload],
    staleTime: 0,
    enabled: !!payload,
    refetchOnWindowFocus: false
  });

export const useUpdateTransportRosterMutation = () =>
  useMutation<
    ApiResponse,
    Error,
    { id: number; data: Record<string, unknown> }
  >({
    mutationFn: (payload: { id: number; data: Record<string, unknown> }) => {
      return put(
        `${API_ENDPOINTS.TRANSPORT_ROASTER}/${payload.id}`,
        payload.data
      );
    },
    onSuccess: handleResponse,
    onError: error => appHandleError(error)
  });
